<script setup lang="ts">
  import { useMagicKeys, whenever } from '@vueuse/core';
  import { storeToRefs } from 'pinia';
  import pluralize from 'pluralize';
  import { computed, inject, onBeforeMount, ref } from 'vue';

  import type {
    Call,
    CommunicationChannel,
    Contact,
    Email,
    EmailThread,
    Partner,
    Task,
  } from '@stuut/api/models';
  import { formatPhoneNumber } from '@stuut/common/src/utils';
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from '@ui/components/alert-dialog';
  import { Badge } from '@ui/components/badge';
  import { Button } from '@ui/components/button';
  import { toast } from '@ui/components/sonner';
  import { Switch } from '@ui/components/switch';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import { stuut } from '@/api';
  import ContactCards from '@/components/contacts/ContactCards.vue';
  import { EmailThreadDrawer } from '@/components/email-threads';
  import { useProfile, useTaskCollectionStore, useTaskStore } from '@/store';

  const showEmailThread = ref(false);
  const closeDrawer = inject<() => void>('closeDrawer');
  const showCompleteTaskPrompt = ref(false);
  const skipCompleteTaskPrompt = ref(false);
  const profileStore = useProfile();
  const taskStore = useTaskStore();
  const taskCollectionStore = useTaskCollectionStore();
  const { currentTask } = storeToRefs(taskCollectionStore);
  const isSubmitting = ref(false);
  const ContactCardsRef = ref();
  const email = ref<Email | null>(null);
  const taskCall = ref<Call>();
  const emailThread = ref<EmailThread | null>(null);
  const loadingEmail = ref<boolean>(false);
  const isShowEmail = ref(false);
  const showConfirmDialog = ref(false);
  const emailId = ref<string | undefined>(currentTask?.value?.acknowledgeTaskData?.data?.email_id);
  const contactTypeMap = {
    call: 'phone number',
    email: 'email address',
  };

  const partner = ref<Partner>();
  const undoing = ref(false);
  const disabling = ref(false);
  const primaryEmailContactId = ref<string>();
  const primaryPhoneContactId = ref<string>();

  const primaryContactId = computed(() =>
    source.value === 'email'
      ? partner.value?.primaryEmailContact?.id
      : partner.value?.primaryPhoneContact?.id,
  );

  const source = computed<CommunicationChannel>(
    () =>
      (currentTask?.value?.acknowledgeTaskData?.data?.source as CommunicationChannel) || 'email',
  );

  const partnerId = ref<string | null>(currentTask?.value?.partner_id ?? null);

  const newPrimaryContactObj = ref(
    currentTask?.value?.acknowledgeTaskData?.data?.new_primary_contact,
  );

  const isCallSource = computed(() => source.value === 'call');
  const isEmailSource = computed(() => source.value === 'email');

  const totalContactsAutoDisabled = computed(() => contactsAutoDisabled.value?.length || 0);
  const totalPendingActionCount = computed(() => pendingActionContacts.value?.length || 0);
  const additionalPendingActionCount = computed(() =>
    totalPendingActionCount.value ? totalPendingActionCount.value - 1 : 0,
  );

  const didNotDisable = computed(() =>
    currentTask?.value?.acknowledgeTaskData?.data?.reason?.startsWith('LLM did not disable:'),
  );

  const contactValue = computed(() => {
    const value = currentTask?.value?.acknowledgeTaskData?.data?.contact_value || '';

    if (source.value === 'call') return formatPhoneNumber(value);

    return value;
  });

  const hasActiveRequest = computed(() => undoing.value || isSubmitting.value || disabling.value);

  const contactCommunicationDisabledActions = computed(() => {
    return currentTask?.value?.acknowledgeTaskData?.data?.disable_communication_actions || [];
  });

  const pendingActions = computed(() => {
    return (
      contactCommunicationDisabledActions.value?.filter((action) => !action.auto_disable) || []
    );
  });

  const contactsAutoDisabledActions = computed(() => {
    return contactCommunicationDisabledActions.value?.filter((action) => action.auto_disable) || [];
  });

  const partnerAutoDisabledAction = computed(() =>
    contactCommunicationDisabledActions.value?.find((action) => action?.id?.startsWith('ptn_')),
  );

  const contactsAutoDisabled = computed(() => {
    return contactsAutoDisabledActions.value?.map((action) => {
      if (action.id?.startsWith('ptn_')) {
        return partner.value?.allContacts?.find((contact) => contact?.id === '-1') as Contact;
      }

      return partner.value?.allContacts?.find((contact) => contact?.id === action.id) as Contact;
    });
  });

  const pendingActionContacts = computed(() => {
    return pendingActions.value?.map((action) => {
      if (action.id?.startsWith('ptn_')) {
        return partner.value?.allContacts?.find((contact) => contact?.id === '-1') as Contact;
      }

      return partner.value?.allContacts?.find((contact) => contact?.id === action.id) as Contact;
    });
  });

  const openCompleteTaskPrompt = () => {
    if (skipCompleteTaskPrompt.value) return;

    showCompleteTaskPrompt.value = true;
  };

  const closeConfirmDialog = () => {
    showConfirmDialog.value = false;
  };

  const hasPrimaryContactChanged = computed(() => {
    const currentPrimaryValue =
      source.value === 'email' ? partner.value?.primaryEmail : partner.value?.primaryPhone;

    return Boolean(
      newPrimaryContactObj.value?.contact_value &&
      newPrimaryContactObj.value?.contact_value !== currentPrimaryValue,
    );
  });

  const handleDisableClick = async () => {
    const promises: Promise<Partner | Contact>[] = [];

    disabling.value = true;

    try {
      if (partnerAutoDisabledAction.value?.id) {
        await stuut.partners.disable(
          partner.value as Partner,
          partner.value?.partnerAccountContact as Contact,
          source.value,
          'contact_disable_task',
        );
      }

      pendingActionContacts.value?.forEach((contact) => {
        if (!partner.value || !contact?.id) return;

        promises.push(
          stuut.partners.disable(partner.value, contact, source.value, 'contact_disable_task'),
        );
      });

      await Promise.all(promises);
      toast.success('Contact disabled');
    } catch (err: any) {
      toast.error(err?.clientMessage || 'An error occurred');
    } finally {
      disabling.value = false;
    }
  };

  const handleUndoClick = async () => {
    const promises: Promise<Partner | Contact>[] = [];

    undoing.value = true;

    try {
      if (partnerAutoDisabledAction.value?.id) {
        await stuut.partners.enable(
          partner.value as Partner,
          partner.value?.partnerAccountContact as Contact,
          source.value,
          'contact_disable_task',
        );
      }

      contactsAutoDisabledActions.value?.forEach((contact) => {
        if (!contact?.id || contact?.id?.startsWith('ptn_') || !partner.value) {
          return;
        }

        promises.push(
          stuut.partners.enable(
            partner.value,
            (partner.value?.contacts?.find((c) => c.id === contact?.id) as Contact) ||
              (contact as unknown as Contact),
            source.value,
            'contact_disable_task',
          ),
        );
      });

      await Promise.all(promises);

      toast.success('Contact has been re-enabled');
    } catch (err: any) {
      toast.error(err?.clientMessage || 'An error occurred');
    } finally {
      undoing.value = false;
    }
  };

  const isButtonDisabled = computed(() => !currentTask?.value?.isOpen || hasActiveRequest.value);

  const audioPlayerRef = ref();
  const keys = useMagicKeys();

  const activeContacts = computed(() => {
    const contacts =
      source.value === 'email'
        ? partner.value?.activeEmailContacts
        : partner.value?.activePhoneContacts;

    return contacts?.sort((a, b) => a.name.localeCompare(b.name));
  });

  const openConfirmDialog = () => {
    showConfirmDialog.value = true;
  };

  const completeTask = async () => {
    isSubmitting.value = true;

    try {
      const response = await stuut.tasks.handleCompleteAction(currentTask?.value as Task);

      toast.success('Acknowledged task and marked complete.');

      taskCollectionStore.afterCompleteTask();
      taskCollectionStore.refreshAllTaskTables();

      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      isSubmitting.value = false;
    }
  };

  const afterNewContactSave = async (newContact: Contact) => {
    await setPrimaryContact(newContact);

    if (newContact?.is_primary_email || newContact?.is_primary_phone) {
      openCompleteTaskPrompt();
    }
  };

  const openContactEditor = () => {
    ContactCardsRef.value?.openContactEditor?.();
  };

  const loadEmail = async () => {
    if (!emailId.value) return;

    loadingEmail.value = true;

    try {
      email.value = await stuut?.emails?.retrieve(emailId.value);

      if (email.value?.thread_id) {
        emailThread.value = await stuut.emailThreads.retrieve(email.value?.thread_id);
      }
    } catch (error) {
      console.error(error);
    } finally {
      loadingEmail.value = false;
    }
  };

  const setPrimaryContact = async (contact) => {
    if (!partner.value) return;

    if (source.value === 'call') {
      primaryPhoneContactId.value = contact?.id;
    } else {
      primaryEmailContactId.value = contact?.id;
    }

    await stuut.partners
      .setPrimary(partner.value, contact || null, source.value)
      .catch((err) => toast.error(err.clientMessage));

    openCompleteTaskPrompt();
  };

  whenever(keys.space, () => {
    const tagName = document.activeElement?.tagName;

    if (
      // @ts-ignore
      ['INPUT', 'SELECT', 'TEXTAREA'].includes(tagName) ||
      // @ts-ignore
      document.activeElement?.isContentEditable
    ) {
      return;
    }

    audioPlayerRef.value?.wavesurfer?.playPause?.();
  });

  onBeforeMount(async () => {
    isShowEmail.value = Boolean(didNotDisable.value);

    if (partnerId.value) {
      partner.value = await taskStore.peekFindPartner(partnerId.value);

      primaryEmailContactId.value = partner.value?.primaryEmailContact?.id;
      primaryPhoneContactId.value = partner.value?.primaryPhoneContact?.id;
    }

    if (emailId.value) await loadEmail();

    if (currentTask?.value?.acknowledgeTaskData?.data?.call_id) {
      taskCall.value = await stuut.calls.retrieve(
        currentTask?.value?.acknowledgeTaskData?.data?.call_id,
      );
    }
  });

  defineExpose({
    openContactEditor,
  });
</script>

<template>
  <div class="space-y-4">
    <div
      class="space-y-8"
      :class="{
        'opacity-40': !currentTask?.isOpen,
      }"
    >
      <div v-if="totalContactsAutoDisabled" class="space-y-2.5">
        <div class="space-y-2.5">
          <div class="flex items-center space-x-2">
            <i class="far fa-address-card"></i>
            <strong class="text-muted-foreground">Disabled contact(s)</strong>
          </div>

          <div v-if="contactsAutoDisabled?.length">
            <div
              v-for="contact in contactsAutoDisabled"
              :key="contact?.id"
              class="grid grid-cols-[auto_1fr_1fr_1fr_auto] gap-x-5 h-10 border-b items-center"
            >
              <i
                v-if="isCallSource && contact?.is_phone_disabled"
                class="fas fa-ban text-error text-lg!"
              ></i>

              <i
                v-if="isEmailSource && contact?.is_email_disabled"
                class="fas fa-ban text-error text-lg!"
              ></i>

              <span>{{ contact?.name }}</span>

              <span :class="isEmailSource && contact?.is_email_disabled && 'line-through'">
                {{ contact?.email }}
              </span>

              <span :class="isCallSource && contact?.is_phone_disabled && 'line-through'">
                {{ formatPhoneNumber(contact?.phone || '') }}
              </span>

              <Tooltip v-if="contactCommunicationDisabledActions?.[0]?.auto_disable">
                <TooltipTrigger>
                  <Button
                    v-if="
                      (isEmailSource && contact?.is_email_disabled) ||
                      (isCallSource && contact?.is_phone_disabled)
                    "
                    size="inline"
                    variant="link"
                    class="text-xs text-blue font-bold"
                    :disabled="isButtonDisabled"
                    :loading="undoing"
                    @click="openConfirmDialog"
                  >
                    Re-enable
                  </Button>
                </TooltipTrigger>
              </Tooltip>

              <Button
                v-if="
                  !(isEmailSource && contact?.is_email_disabled) &&
                  !(isCallSource && contact?.is_phone_disabled)
                "
                size="inline"
                variant="link"
                class="text-xs text-destructive font-bold"
                :disabled="isButtonDisabled"
                @click="handleDisableClick"
              >
                Disable contact
              </Button>
            </div>
          </div>
        </div>

        <div
          v-if="!profileStore.profile.organization?.is_auto_set_primary_enabled"
          class="text-2xs text-muted-foreground"
        >
          Update your

          <RouterLink
            class="text-blue hover:underline"
            :to="{ name: 'agents.collection.primary-contact' }"
          >
            <Button variant="link" size="inline" @click="closeDrawer">
              collection agent settings
            </Button>
          </RouterLink>

          to automatically assign a new primary contact when the current one becomes disabled.
        </div>
      </div>

      <div v-if="totalPendingActionCount" class="flex items-start space-x-3">
        <i class="fa-solid fa-circle-question text-muted-foreground mt-1"></i>

        <div>
          <p>
            <strong class="text-muted-foreground">Pending</strong>
          </p>

          <p>
            <strong>{{ contactValue }}</strong>
            <br />
            <Button
              v-if="didNotDisable"
              size="inline"
              variant="link"
              class="text-xs text-blue font-bold mt-1.5"
              :disabled="isButtonDisabled"
              @click="handleDisableClick"
              >Disable contact
            </Button>

            <template v-if="additionalPendingActionCount">
              <br />
              <span class="text-muted-foreground">
                (and
                <strong>{{ additionalPendingActionCount }}</strong>
                matching
                {{ pluralize(contactTypeMap[source], additionalPendingActionCount) }})
              </span>
            </template>
          </p>
        </div>
      </div>

      <div v-if="newPrimaryContactObj?.contact_value" class="space-y-2.5">
        <div class="flex items-center">
          <div class="w-6">
            <i class="far fa-check-circle text-success text-base mt-1"></i>
          </div>

          <strong class="text-muted-foreground">Updated Primary</strong>
        </div>

        <p class="ml-6" :class="hasPrimaryContactChanged && 'line-through'">
          <strong class="space-x-2">
            <span>{{ formatPhoneNumber(newPrimaryContactObj.contact_value || '') }}</span>

            <Tooltip v-if="hasPrimaryContactChanged" :delay-duration="0">
              <TooltipTrigger>
                <i class="far fa-info-circle text-xs" />
              </TooltipTrigger>

              <TooltipContent> Primary contact was updated to a new value. </TooltipContent>
            </Tooltip>
          </strong>
        </p>
      </div>

      <div class="space-y-2.5">
        <div class="flex items-center space-x-2">
          <i class="far fa-circle-exclamation text-destructive text-base"></i>
          <strong class="text-muted-foreground">Set a new primary {{ source }}</strong>
        </div>

        <div>
          <div class="grid grid-cols-[1fr_1fr_1fr_85px] h-10 items-center border-b">
            <strong class="text-muted-foreground">Name</strong>
            <strong class="text-muted-foreground">Email</strong>
            <strong class="text-muted-foreground">Phone</strong>
            <strong class="text-muted-foreground text-center capitalize text-nowrap"
              >Set Primary</strong
            >
          </div>

          <template v-if="activeContacts?.length">
            <div
              v-for="contact in activeContacts"
              :key="contact?.id"
              class="grid grid-cols-[1fr_1fr_1fr_75px] h-10 border-b items-center"
            >
              <span
                :class="{
                  'font-bold': primaryContactId === contact?.id,
                  'opacity-50': isEmailSource
                    ? contact?.is_email_disabled || !contact?.email
                    : contact?.is_phone_disabled || !contact?.phone,
                }"
              >
                {{ contact?.name }}
              </span>

              <span
                class="space-x-2"
                :class="{
                  'font-bold': isEmailSource && primaryContactId === contact?.id,
                  'opacity-50': isEmailSource
                    ? contact?.is_email_disabled || !contact?.email
                    : contact?.is_phone_disabled || !contact?.phone,
                }"
              >
                <span>{{ contact?.email }}</span>

                <Badge
                  v-if="source === 'email' && contact?.is_primary_email"
                  variant="agent"
                  class="rounded-full size-6 text-center my-auto"
                  size="xs"
                >
                  <i class="fa far fa-envelope" />
                </Badge>
              </span>

              <span
                class="space-x-2"
                :class="{
                  'font-bold': isCallSource && primaryContactId === contact?.id,
                  'opacity-50': isEmailSource
                    ? contact?.is_email_disabled || !contact?.email
                    : contact?.is_phone_disabled || !contact?.phone,
                }"
              >
                <span>{{ formatPhoneNumber(contact?.phone || '') }}</span>

                <Badge
                  v-if="source === 'call' && contact?.is_primary_phone"
                  variant="agent"
                  class="rounded-full size-6 text-center my-auto"
                  size="xs"
                >
                  <i class="fa far fa-phone" />
                </Badge>
              </span>

              <div class="flex justify-center">
                <Switch
                  class="data-[state=checked]:bg-success!"
                  :model-value="primaryContactId === contact?.id"
                  :disabled="
                    isEmailSource
                      ? contact?.is_email_disabled || !contact?.email
                      : contact?.is_phone_disabled || !contact?.phone
                  "
                  @update:model-value="
                    (value) =>
                      primaryContactId !== contact?.id && setPrimaryContact(value ? contact : null)
                  "
                />
              </div>
            </div>
          </template>
        </div>

        <Button
          class="hover:bg-transparent font-bold"
          variant="ghost"
          size="inline"
          @click="ContactCardsRef?.openContactEditor"
        >
          + Add Contact
        </Button>
      </div>
    </div>

    <div class="hidden">
      <ContactCards
        ref="ContactCardsRef"
        :partner="partner as Partner"
        :contacts="pendingActionContacts"
        :disable-dropdown="!currentTask?.isOpen"
        @after-save="afterNewContactSave"
      />
    </div>

    <AlertDialog :open="showConfirmDialog" @update:open="(status) => (showConfirmDialog = status)">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm</AlertDialogTitle>
          <AlertDialogDescription class="space-y-2.5">
            <p>
              This contact was disabled: <strong>{{ source }}</strong> at
              <strong>{{ contactValue }}</strong
              >.
            </p>

            <p>Are you sure you want to re-enable this contact?</p>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel @click="closeConfirmDialog"> Cancel </AlertDialogCancel>

          <AlertDialogAction
            :disabled="isButtonDisabled"
            :loading="undoing"
            @click="handleUndoClick"
          >
            Re-enable
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <EmailThreadDrawer
      v-if="emailThread"
      v-model:open="showEmailThread"
      :thread="emailThread"
      is-slider
      default-open
      class="h-full min-h-full"
      @close="
        () => {
          emailThread = null;
          showEmailThread = false;
        }
      "
    />

    <AlertDialog
      :open="showCompleteTaskPrompt"
      @update:open="
        (status) => {
          showCompleteTaskPrompt = status;

          if (!showCompleteTaskPrompt) {
            skipCompleteTaskPrompt = true;
          }
        }
      "
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm</AlertDialogTitle>
          <AlertDialogDescription class="space-y-2.5">
            <p>Mark this as complete?</p>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel
            @click="
              () => {
                skipCompleteTaskPrompt = true;
                showCompleteTaskPrompt = false;
              }
            "
          >
            Cancel
          </AlertDialogCancel>

          <AlertDialogAction
            :disabled="isButtonDisabled"
            :loading="isSubmitting"
            @click="completeTask"
          >
            <i class="fas fa-check text-xs"></i>
            <span>Mark as complete</span>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>

<style scoped></style>
